<template>
  <!-- 普通页面：带导航 + 底部 -->
  <div
    v-if="!isBlankLayout"
    class="w-full flex flex-col min-h-screen"
  >
    <!-- 导航栏区域 - 固定定位，覆盖在内容上方 -->
    <div
      ref="navbarRef"
      class="fixed w-full left-0 top-0 z-50 transition-all duration-300 ease-in-out not-scrolled_header"
    >
      <div
        class="w-full backdrop-blur-sm transition-all duration-500 ease-out"
        :style="navbarInnerStyle"
      >
        <Navbar :text-scale="textScale" :logo-style="logoStyle" />
      </div>
    </div>
    <!-- 子路由内容显示区域 -->
    <div
      class="flex-1"
      :style="contentStyle"
    >
      <router-view />
    </div>
    <!-- 底部组件 -->
    <Footer />
    <!-- 快捷联系组件 -->
    <ContactQuickAccess />
  </div>

  <!-- 空白布局页面：全屏内容，不显示导航/底部（用于营销 H5 等） -->
  <div
    v-else
    class="w-full min-h-screen"
  >
    <router-view />
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { useRoute } from "vue-router";
import Navbar from "./components/Navbar.vue";
import Footer from "./components/Footer.vue";
import ContactQuickAccess from "@/components/ContactQuickAccess.vue";

const route = useRoute();

// 是否使用空白布局（如营销 H5）
const isBlankLayout = computed(() => route.meta.layout === "blank");

// 导航栏引用
const navbarRef = ref<HTMLElement | null>(null);
const navbarHeight = ref(0);
const windowWidth = ref(0);

// 滚动相关状态
const scrollY = ref(0);
const lastScrollY = ref(0);
const isScrollingUp = ref(false);

// 基础尺寸
const basePaddingTop = 8;
const basePaddingBottom = 29;
const baseLogoWidth = 120;
const baseLogoHeight = 59;

// 缩放比例（一阶变化：上滑缩小，下滑恢复）
const scale = computed(() => {
  // 只有两种状态：上滑时缩小到 0.9，下滑时恢复到 1.0
  return isScrollingUp.value && scrollY.value > 50 ? 0.9 : 1.0;
});

// 文字缩放比例（与导航栏同步）
const textScale = computed(() => scale.value);

// 导航栏内层样式
const navbarInnerStyle = computed(() => {
  const paddingTop = basePaddingTop * scale.value;
  const paddingBottom = basePaddingBottom * scale.value;
  
  // 移动端（小于 lg 断点 1024px）使用 #97A3C5
  let backgroundColor;
  if (windowWidth.value > 0 && windowWidth.value < 1024) {
    backgroundColor = '#97A3C5';
  } else {
    // 桌面端：当页面滚动后，背景颜色从白色半透明改为 #919DC2
    backgroundColor = scrollY.value > 0 ? '#919DC2' : 'rgba(255, 255, 255, 0.5)';
  }
  
  return {
    paddingTop: `${paddingTop}px`,
    paddingBottom: `${paddingBottom}px`,
    backgroundColor: backgroundColor,
  };
});

// Logo 样式
const logoStyle = computed(() => {
  const width = baseLogoWidth * scale.value;
  const height = baseLogoHeight * scale.value;
  return {
    width: `${width}px`,
    height: `${height}px`,
  };
});

// 更新导航栏高度
const updateNavbarHeight = () => {
  if (navbarRef.value) {
    navbarHeight.value = navbarRef.value.offsetHeight;
  }
};

// 更新窗口宽度
const updateWindowWidth = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth;
  }
};

// 内容区域样式 - 移动端添加顶部边距（空白布局不需要）
const contentStyle = computed(() => {
  if (isBlankLayout.value) {
    return {};
  }
  // 只在移动端（小于 lg 断点 1024px）添加 margin-top
  if (windowWidth.value > 0 && windowWidth.value < 1024) {
    return {
      marginTop: `${navbarHeight.value}px`,
    };
  }
  return {};
});

// 滚动处理函数（使用节流优化性能）
let scrollTimer: ReturnType<typeof setTimeout> | null = null;

const handleScroll = () => {
  // 清除之前的定时器
  if (scrollTimer) {
    clearTimeout(scrollTimer);
  }

  // 节流处理，每16ms执行一次（约60fps）
  scrollTimer = setTimeout(() => {
    const currentScrollY = window.scrollY || window.pageYOffset;

    // 判断滚动方向（使用阈值避免微小滚动触发）
    const scrollThreshold = 5;
    if (currentScrollY > lastScrollY.value + scrollThreshold && currentScrollY > 50) {
      // 向下滚动（上滑）
      isScrollingUp.value = true;
    } else if (currentScrollY < lastScrollY.value - scrollThreshold) {
      // 向上滚动（下滑）
      isScrollingUp.value = false;
    }

    scrollY.value = currentScrollY;
    lastScrollY.value = currentScrollY;
  }, 16);
};

// resize 处理函数
const handleResize = () => {
  updateWindowWidth();
  updateNavbarHeight();
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true });
  scrollY.value = window.scrollY || window.pageYOffset;
  lastScrollY.value = scrollY.value;
  
  // 初始化窗口宽度和导航栏高度
  updateWindowWidth();
  updateNavbarHeight();
  
  // 监听窗口大小变化
  window.addEventListener('resize', handleResize);
  
  // 使用 MutationObserver 监听导航栏高度变化（因为缩放可能导致高度变化）
  if (navbarRef.value) {
    const observer = new MutationObserver(updateNavbarHeight);
    observer.observe(navbarRef.value, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: true,
      subtree: true,
    });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', handleResize);
});
</script>